import { RiMailLine, RiSmartphoneLine, RiWhatsappLine } from "react-icons/ri";
import type { IconType } from "react-icons";
import type { RecoveryChannel } from "../types/password-recovery.dto";

/**
 * Rótulos e textos dos canais de recuperação de senha.
 *
 * A DISPONIBILIDADE não está aqui: vem do back (`GET /auth/password/channels`),
 * que sabe quais provedores estão configurados. Este arquivo é só a copy.
 *
 * A ordem do array é a ordem de exibição — WhatsApp primeiro por ser o único
 * canal com envio integrado hoje.
 *
 * O painel de "alterar senha" do perfil (`useChangePassword`) usa estas mesmas
 * constantes — a lista duplicada que existia lá foi removida.
 */

export interface RecoveryChannelCopy {
    channel: RecoveryChannel;
    /** Rótulo da opção de escolha do método. */
    label: string;
    Icon: IconType;
    /** Frase do passo do código, completada com o destino mascarado. */
    sentTo: (destination: string) => string;
    /** Texto do link de reenvio. */
    resend: string;
    /** Motivo exibido quando o back informa que o canal não está disponível. */
    unavailable: string;
}

export const RECOVERY_CHANNELS: RecoveryChannelCopy[] = [
    {
        channel: "whatsapp",
        label: "Receber código por WhatsApp",
        Icon: RiWhatsappLine,
        sentTo: (destination) => `Enviamos um código pelo WhatsApp para ${destination}.`,
        resend: "Reenviar pelo WhatsApp",
        unavailable: "Indisponível nesta loja",
    },
    {
        channel: "email",
        label: "Receber código por e-mail",
        Icon: RiMailLine,
        sentTo: (destination) => `Enviamos um código para o e-mail ${destination}.`,
        resend: "Reenviar e-mail",
        unavailable: "Indisponível nesta loja",
    },
    {
        channel: "sms",
        label: "Receber código por SMS",
        Icon: RiSmartphoneLine,
        sentTo: (destination) => `Enviamos um código por SMS para ${destination}.`,
        resend: "Reenviar SMS",
        unavailable: "Indisponível nesta loja",
    },
];

export function recoveryChannelCopy(channel: RecoveryChannel): RecoveryChannelCopy {
    return RECOVERY_CHANNELS.find((item) => item.channel === channel) ?? RECOVERY_CHANNELS[0]!;
}

/** Dígitos do código enviado — igual ao `generateVerificationCode()` do back. */
export const RECOVERY_CODE_LENGTH = 5;

/** Mínimo da nova senha — o back valida o mesmo (8 a 50 caracteres). */
export const NEW_PASSWORD_MIN_LENGTH = 8;

/**
 * Espera entre dois envios para o mesmo destino. É o MESMO valor do
 * `resendCooldownSeconds` do back (`PasswordRecoveryService`) — o botão de
 * reenviar fica desabilitado nesse intervalo em vez de deixar o cliente clicar
 * e tomar erro.
 */
export const RESEND_COOLDOWN_SECONDS = 30;

/**
 * Espera depois de um 429 ("Muitas solicitações de código"): o back limita a
 * 5 códigos por cliente por hora. Sem um `retryAfterSeconds` na resposta, o
 * botão de reenviar fica travado por este tempo — clicar de novo só repetiria
 * o erro.
 */
export const CODE_LIMIT_COOLDOWN_SECONDS = 5 * 60;
